/*
 * Copyright (c) 2022. American Express Company
 */

// theirs
import React, { PureComponent, ReactComponentElement } from 'react';

// ours
import { TypographyViewModel } from 'src/types/view-models/typography.view.model';
import { TypographyVariants } from 'src/types/typography-variants.enum';

// styles
import styles from './Typography.module.scss';

export default class Typography extends PureComponent<TypographyViewModel> {
    public override render(): ReactComponentElement<any> {
        const VariantComponent: keyof JSX.IntrinsicElements =
            this.props.component || Typography.getComponent(this.props.variant);
        return (
            <VariantComponent
                className={this.getClasses()}
                id={this.props.id}
                style={this.props.style}
                /* eslint-disable-next-line @typescript-eslint/no-misused-promises */
                onClick={this.props.onClick}
                /* eslint-disable-next-line @typescript-eslint/no-misused-promises */
                onDoubleClick={this.props.onDoubleClick}
                data-cy={this.props['data-cy']}
                aria-live={this.props['aria-live']}
                aria-busy={this.props['aria-busy']}
                aria-hidden={this.props['aria-hidden']}
            >
                {this.props.children}
            </VariantComponent>
        );
    }

    private static getComponent(variant: TypographyVariants): keyof JSX.IntrinsicElements {
        switch (variant) {
            case TypographyVariants.HEADING_XL:
                return 'h1';
            case TypographyVariants.HEADING_L:
                return 'h2';
            case TypographyVariants.HEADING_M:
                return 'h3';
            case TypographyVariants.HEADING_S:
                return 'h4';
            case TypographyVariants.HEADING_XS:
                return 'h6';
            case TypographyVariants.BODY:
            case TypographyVariants.BODY_BOLD:
            case TypographyVariants.LINK:
            case TypographyVariants.SUBTEXT:
            case TypographyVariants.SUBTEXT_BOLD:
                return 'span';
            case TypographyVariants.CAPTION:
            case TypographyVariants.LEGAL_PRIMARY:
            case TypographyVariants.LEGAL_SECONDARY:
            case TypographyVariants.LEGAL_TERTIARY:
            default:
                return 'span';
        }
    }

    private getClasses(): string {
        const classes: string[] = [];

        if (this.props.className) {
            classes.push(this.props.className);
        }

        if (this.props.noWrap) {
            classes.push(styles.noWrap);
        }

        if (this.props.gutterBottom) {
            classes.push(styles.gutterBottom);
        }

        if (this.props.white) {
            classes.push(styles.whiteText);
        }

        switch (this.props.variant) {
            case TypographyVariants.HEADING_XL:
                classes.push(styles.headingXL);
                break;
            case TypographyVariants.HEADING_L:
                classes.push(styles.headingL);
                break;
            case TypographyVariants.HEADING_M:
                classes.push(styles.headingM);
                break;
            case TypographyVariants.HEADING_S:
                classes.push(styles.headingS);
                break;
            case TypographyVariants.HEADING_XS:
                classes.push(styles.headingXS);
                break;
            case TypographyVariants.LINK:
                classes.push(styles.link);
                break;
            case TypographyVariants.LINK_2:
                classes.push(styles.link2);
                break;
            case TypographyVariants.SUBTEXT:
                classes.push(styles.subtext);
                break;
            case TypographyVariants.SUBTEXT_BOLD:
                classes.push(styles.subtextBold);
                break;
            case TypographyVariants.BODY:
                classes.push(styles.body);
                break;
            case TypographyVariants.BODY_BOLD:
                classes.push(styles.bodyBold);
                break;
            case TypographyVariants.CAPTION:
                classes.push(styles.caption);
                break;
            case TypographyVariants.LEGAL_PRIMARY:
                classes.push(styles.legalPrimary);
                break;
            case TypographyVariants.LEGAL_SECONDARY:
                classes.push(styles.legalSecondary);
                break;
            case TypographyVariants.LEGAL_TERTIARY:
                classes.push(styles.legalTertiary);
                break;
        }

        return classes.join(' ');
    }
}
